import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, ChevronDown, Loader2, X } from 'lucide-react';
import { IconRefresh, IconUserPlus } from '@tabler/icons-react';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { buildFilesScopeFromRow } from './CustomerFilesPanel';
import UserPortalFormPanel from './UserPortalFormPanel';
import '@fontsource-variable/geist/wght.css';
import '../../process-config/ConfigStatusView2.css';
import '../../process-config/components/CondicoesIntervencaoTableV2.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import './UsersCustomersView.css';
import './CustomersCadastroUsuariosView.css';

const UC_SEM_GRUPOS = {
  key: 'semGrupos',
  label: 'Sem grupos',
  desc: 'Clientes individuais',
  color: '#0E8A82',
};

const UC_GRUPO_COLOR = '#6E56CF';

const CAD_PENDING_GROUPS = [
  {
    key: 'comContrato',
    label: 'Com contrato',
    desc: 'Cadastros com código de contrato informado',
    color: '#0E8A82',
  },
  {
    key: 'semContrato',
    label: 'Sem contrato',
    desc: 'Cadastros sem código de contrato',
    color: '#767889',
  },
];

const CAD_MAIL_BLOCK_TOOLTIP =
  'É necessário configurar o e‑mail da empresa para que as credenciais de acesso sejam enviadas corretamente ao usuário solicitante.';

function onlyDigits(v) {
  return String(v ?? '').replace(/\D/g, '');
}

function hasContractCodigo(row) {
  return String(row?.contractCodigo || '').trim().length > 0;
}

function formatDocByKind(digits, kind) {
  const d = onlyDigits(digits);
  const k = String(kind || '').toLowerCase();
  if (k === 'cpf' || d.length === 11) {
    if (d.length !== 11) return d || '—';
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (k === 'cnpj' || d.length === 14) {
    if (d.length !== 14) return d || '—';
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return d || '—';
}

function formatCreatedAt(raw) {
  if (!raw) return '—';
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(
    dt.getMinutes()
  )}`;
}

function rowStableKey(r) {
  return `${r.idGrupoRef || ''}|${r.primeiro?.codigo || ''}|${(r.outros || []).map((o) => o.codigo).join(',')}`;
}

function mapListagemRows(data) {
  const rows = data?.rows || [];
  return rows.map((r, i) => ({
    primeiro: r.primeiro,
    grupoNome: r.grupoNome,
    outros: r.outros || [],
    hasGrupoVisual: r.hasGrupoVisual,
    idGrupoRef: r.idGrupoRef,
    totalUsuarios: Number(r.totalUsuarios) || 0,
    rowKey: `${rowStableKey(r)}#${i}`,
  }));
}

function rowMatchesDocument(row, documentDigits) {
  const doc = onlyDigits(documentDigits);
  if (!doc) return true;
  const codes = [row.primeiro, ...(row.outros || [])].filter(Boolean);
  return codes.some((c) => onlyDigits(c.cnpj) === doc);
}

function normalizeSearch(v) {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

async function apiVc(path, { method = 'GET', body } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/vc-usr${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

/**
 * Processos → Cadastro de usuários (pendentes MySQL vc_auth_userspending).
 */
export default function CustomersCadastroUsuariosView({ onPendingChange, showRefresh = false } = {}) {
  const [phase, setPhase] = useState('pending'); // pending | pick-client
  const [pendingRows, setPendingRows] = useState([]);
  const [defaultPassword, setDefaultPassword] = useState('mudar@123@');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [clientRows, setClientRows] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [semGruposCollapsed, setSemGruposCollapsed] = useState(true);

  const [userForm, setUserForm] = useState(null); // { scope, pending, draft }
  const [userFormEntered, setUserFormEntered] = useState(false);
  const [mailBlockTooltip, setMailBlockTooltip] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const selectedPending = useMemo(
    () => pendingRows.find((r) => r.id === selectedId) || null,
    [pendingRows, selectedId]
  );

  const selectedEmailExists = Boolean(selectedPending?.emailAlreadyExists);

  const filteredPending = useMemo(() => {
    const q = normalizeSearch(searchQuery);
    if (!q) return pendingRows;
    return pendingRows.filter((row) =>
      normalizeSearch(
        [
          formatCreatedAt(row.createdAt),
          row.fullName,
          row.email,
          formatDocByKind(row.documentDigits, row.documentKind),
          row.contractCodigo,
          row.observations,
        ].join(' ')
      ).includes(q)
    );
  }, [pendingRows, searchQuery]);

  const groupedPending = useMemo(() => {
    const map = { comContrato: [], semContrato: [] };
    filteredPending.forEach((row) => {
      if (hasContractCodigo(row)) map.comContrato.push(row);
      else map.semContrato.push(row);
    });
    return map;
  }, [filteredPending]);

  const groupsWithRows = useMemo(
    () => CAD_PENDING_GROUPS.filter((g) => (groupedPending[g.key] || []).length > 0),
    [groupedPending]
  );

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const allCollapsed = useMemo(
    () => groupsWithRows.length > 0 && groupsWithRows.every((g) => !!collapsedGroups[g.key]),
    [groupsWithRows, collapsedGroups]
  );

  const toggleAllGroups = useCallback(() => {
    const nextAllCollapsed = !allCollapsed;
    const next = {};
    groupsWithRows.forEach((g) => {
      next[g.key] = nextAllCollapsed;
    });
    setCollapsedGroups(next);
  }, [allCollapsed, groupsWithRows]);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const j = await apiVc('/users-pending');
      const rows = Array.isArray(j.data) ? j.data : [];
      setPendingRows(rows);
      if (j.defaultPassword) setDefaultPassword(String(j.defaultPassword));
      if (typeof onPendingChange === 'function') onPendingChange(rows.length);
    } catch (e) {
      setPendingRows([]);
      setError(e.message || 'Erro ao carregar cadastros pendentes');
    } finally {
      setLoading(false);
    }
  }, [onPendingChange]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    const onRefresh = () => {
      loadPending();
    };
    window.addEventListener('painel-os-customers-cadastro-refresh', onRefresh);
    return () => window.removeEventListener('painel-os-customers-cadastro-refresh', onRefresh);
  }, [loadPending]);

  useEffect(() => {
    if (!userForm) {
      setUserFormEntered(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setUserFormEntered(true));
    return () => cancelAnimationFrame(id);
  }, [userForm]);

  const hasSelection = Boolean(selectedPending);

  const openConcluir = useCallback(async () => {
    if (!selectedPending || selectedPending.emailAlreadyExists) return;
    setPhase('pick-client');
    setClientLoading(true);
    setClientError(null);
    try {
      const j = await apiVc('/listagem');
      const mapped = mapListagemRows(j.data);
      const filtered = mapped.filter((row) => rowMatchesDocument(row, selectedPending.documentDigits));
      setClientRows(filtered);
      setClientSearchQuery('');
      setExpandedKeys(new Set());
      setSemGruposCollapsed(true);
    } catch (e) {
      setClientRows([]);
      setClientError(e.message || 'Erro ao carregar clientes');
    } finally {
      setClientLoading(false);
    }
  }, [selectedPending]);

  const backToPending = useCallback(() => {
    setPhase('pending');
    setClientRows([]);
    setClientError(null);
    setClientSearchQuery('');
    setUserForm(null);
  }, []);

  const filteredClients = useMemo(() => {
    const q = normalizeSearch(clientSearchQuery);
    if (!q) return clientRows;
    return clientRows.filter((r) =>
      normalizeSearch(
        [
          r.primeiro?.codigo,
          r.primeiro?.cnpj,
          r.primeiro?.nomePortal,
          r.primeiro?.razaoSocial,
          r.grupoNome,
          r.idGrupoRef,
          ...(r.outros || []).flatMap((o) => [o.codigo, o.cnpj, o.nomePortal, o.razaoSocial]),
        ]
          .filter(Boolean)
          .join(' ')
      ).includes(q)
    );
  }, [clientRows, clientSearchQuery]);

  const groupedClients = useMemo(() => {
    const grupos = [];
    const semGrupos = [];
    for (const row of filteredClients) {
      if (row.hasGrupoVisual) grupos.push(row);
      else semGrupos.push(row);
    }
    return { grupos, semGrupos };
  }, [filteredClients]);

  const mailReadyPath = useCallback((pendingId, codigosCliente) => {
    const base = `/users-pending/${pendingId}/mail-ready`;
    const codes = Array.isArray(codigosCliente) ? codigosCliente.filter(Boolean) : [];
    if (!codes.length) return base;
    return `${base}?codigos=${encodeURIComponent(codes.join(','))}`;
  }, []);

  const openUserFormForRow = useCallback(
    (row) => {
      if (!selectedPending) return;
      const scope = buildFilesScopeFromRow(row);
      if (!scope) return;
      setMailBlockTooltip(null);
      setUserForm({
        scope,
        pending: selectedPending,
        draft: {
          nome: selectedPending.fullName,
          email: selectedPending.email,
          senha: defaultPassword,
          foneWhatsapp: selectedPending.phone,
        },
      });
      apiVc(mailReadyPath(selectedPending.id, scope.codigosCliente))
        .then((j) => {
          if (!j?.data?.ready) setMailBlockTooltip(CAD_MAIL_BLOCK_TOOLTIP);
          else setMailBlockTooltip(null);
        })
        .catch(() => {
          setMailBlockTooltip(CAD_MAIL_BLOCK_TOOLTIP);
        });
    },
    [selectedPending, defaultPassword, mailReadyPath]
  );

  const closeUserForm = useCallback(() => {
    if (finishing) return;
    setUserForm(null);
    setMailBlockTooltip(null);
  }, [finishing]);

  const handleSaveUser = useCallback(
    async (payload) => {
      if (!userForm?.pending || !userForm?.scope) return;
      setFinishing(true);
      try {
        const scopeCodigos = userForm.scope.codigosCliente || [];
        const mailCheck = await apiVc(mailReadyPath(userForm.pending.id, scopeCodigos));
        if (!mailCheck?.data?.ready) {
          setMailBlockTooltip(CAD_MAIL_BLOCK_TOOLTIP);
          const err = new Error(CAD_MAIL_BLOCK_TOOLTIP);
          throw err;
        }
        await apiVc('/portal-usuarios', {
          method: 'POST',
          body: {
            idGrupoRef: userForm.scope.idGrupoRef,
            codigos: scopeCodigos,
            nome: payload.nome,
            email: payload.email,
            cargo: payload.cargo,
            foneWhatsapp: payload.foneWhatsapp,
            foneFixo: payload.foneFixo,
            isActive: payload.isActive !== false,
            tipoUsuario: payload.tipoUsuario,
            senha: payload.senha,
            permissoes: payload.permissoes,
            contratoLocais: payload.contratoLocais || [],
          },
        });
        await apiVc(`/users-pending/${userForm.pending.id}/finalize`, {
          method: 'POST',
          body: {
            password: payload.senha || defaultPassword,
            codigos: scopeCodigos,
          },
        });
        setUserForm(null);
        setMailBlockTooltip(null);
        setSelectedId(null);
        setPhase('pending');
        setClientRows([]);
        await loadPending();
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e?.message || e || 'Erro ao salvar'));
      } finally {
        setFinishing(false);
      }
    },
    [userForm, defaultPassword, loadPending, mailReadyPath]
  );

  const renderClientCells = (row, { isSub = false, subCliente = null } = {}) => {
    const c = isSub ? subCliente : row.primeiro;
    if (!c) return null;
    return (
      <>
        <td className="uc-cell-codigo">{c.codigo}</td>
        <td className="uc-cell-cnpj">{c.cnpj || '—'}</td>
        <td className="uc-cell-nome-portal">{c.nomePortal || '—'}</td>
        <td className="uc-cell-razao">{c.razaoSocial || '—'}</td>
        <td className="uc-cell-contratos">{c.contratos ?? 0}</td>
        <td className="uc-cell-usuarios">{isSub ? '—' : row.totalUsuarios ?? 0}</td>
        <td className="uc-cell-action uc-cell-sticky-action">
          {!isSub ? (
            <button
              type="button"
              className="users-customers-add-btn uc-manage-users-add-btn cp-cad-pick-btn"
              onClick={() => openUserFormForRow(row)}
            >
              Selecionar
            </button>
          ) : null}
        </td>
      </>
    );
  };

  return (
    <div className="cp-cadastro-usuarios">
      {phase === 'pending' ? (
        <>
          <div className="cp-cad-toolbar-bar">
            <div className="condicoes-interv-toolbar cp-cad-interv-toolbar">
              {showRefresh ? (
                <EcClickUpFilterChip
                  label="Atualizar"
                  icon={IconRefresh}
                  hasSelection={false}
                  disabled={loading}
                  onClick={() => loadPending()}
                  ariaLabel="Atualizar listagem de cadastros pendentes"
                  className="cp-cad-refresh-btn"
                />
              ) : null}
              <BoardToolbarExpandSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Pesquisar cadastros…"
                ariaLabel="Pesquisar cadastros pendentes"
                forceExpanded
              />
              <EcClickUpFilterChip
                label="Concluir cadastro"
                icon={IconUserPlus}
                hasSelection={hasSelection && !selectedEmailExists}
                disabled={!hasSelection || loading || selectedEmailExists}
                onClick={openConcluir}
                ariaLabel={
                  selectedEmailExists
                    ? 'Não é possível concluir: e-mail já cadastrado neste ambiente'
                    : 'Concluir cadastro do usuário pendente'
                }
                tooltip={
                  selectedEmailExists
                    ? 'E-mail já existente no ambiente. Não é possível concluir este cadastro.'
                    : null
                }
                className={
                  hasSelection && !selectedEmailExists
                    ? 'cp-cad-concluir-btn cp-cad-concluir-btn--attention'
                    : 'cp-cad-concluir-btn'
                }
              />
            </div>
          </div>

          <div className="cp-cad-table-wrap">
            <div className="ci2-root cp-cad-ci2" role="region" aria-label="Cadastros pendentes">
              {loading ? (
                <div className="ci2-panel">
                  <div className="cp-cad-empty">
                    <Loader2 size={18} className="cp-cad-spin" aria-hidden />
                    <span>Carregando cadastros pendentes…</span>
                  </div>
                </div>
              ) : error ? (
                <div className="ci2-panel">
                  <div className="cp-cad-empty cp-cad-empty--err">
                    <span>{error}</span>
                    <button type="button" className="cp-btn cp-btn--primary" onClick={loadPending}>
                      Tentar novamente
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ci2-panel">
                  <div className="ci2-table-scroll cp-cad-ci2-scroll">
                    <table className="ci2-table cp-cad-ci2-table">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Nome</th>
                          <th>Email</th>
                          <th>Documento</th>
                          <th>Contrato</th>
                          <th>Observações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPending.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="cp-cad-td-empty">
                              {pendingRows.length === 0
                                ? 'Nenhum cadastro pendente.'
                                : 'Nenhum resultado para a pesquisa.'}
                            </td>
                          </tr>
                        ) : (
                          CAD_PENDING_GROUPS.map((group) => {
                            const groupRows = groupedPending[group.key] || [];
                            if (!groupRows.length) return null;
                            const collapsed = !!collapsedGroups[group.key];
                            return (
                              <React.Fragment key={group.key}>
                                <tr
                                  className={`ci2-group-row${collapsed ? ' collapsed' : ''}`}
                                  onClick={() => toggleGroup(group.key)}
                                >
                                  <td colSpan={6}>
                                    <div className="ci2-group-label">
                                      <span className="ci2-chev">
                                        <ChevronDown size={13} strokeWidth={2.5} />
                                      </span>
                                      <span
                                        className="ci2-dot"
                                        style={{ background: group.color }}
                                        aria-hidden
                                      />
                                      {group.label}
                                      <span className="ci2-group-count">· {groupRows.length}</span>
                                      <span className="ci2-group-desc">{group.desc}</span>
                                    </div>
                                  </td>
                                </tr>
                                {groupRows.map((row) => {
                                  const sel = selectedId === row.id;
                                  return (
                                    <tr
                                      key={row.id}
                                      className={[
                                        'ci2-data-row',
                                        group.key === 'comContrato' ? 'cat-nada' : 'cat-desabilitada',
                                        'cp-cad-data-row',
                                        collapsed ? 'ci2-hidden-row' : '',
                                        sel ? 'ci2-row-editing cp-cad-row--sel' : '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      onClick={() =>
                                        setSelectedId((prev) => (prev === row.id ? null : row.id))
                                      }
                                      aria-selected={sel}
                                    >
                                      <td>{formatCreatedAt(row.createdAt)}</td>
                                      <td title={row.fullName}>{row.fullName || '—'}</td>
                                      <td className="cp-cad-mono cp-cad-email-cell" title={row.email}>
                                        <span className="cp-cad-email-text">{row.email || '—'}</span>
                                        {row.emailAlreadyExists ? (
                                          <span
                                            className="cp-cad-email-exists-badge"
                                            title="E-mail já cadastrado neste ambiente"
                                          >
                                            Já existe
                                          </span>
                                        ) : null}
                                      </td>
                                      <td className="cp-cad-mono">
                                        {formatDocByKind(row.documentDigits, row.documentKind)}
                                      </td>
                                      <td className="cp-cad-mono">{row.contractCodigo || '—'}</td>
                                      <td
                                        className="cp-cad-obs"
                                        title={row.observations || ''}
                                      >
                                        {row.observations
                                          ? String(row.observations).slice(0, 120)
                                          : '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredPending.length > 0 ? (
                    <div className="ci2-foot-note">
                      <span>
                        {filteredPending.length} cadastro
                        {filteredPending.length !== 1 ? 's' : ''} · {groupsWithRows.length} grupo
                        {groupsWithRows.length !== 1 ? 's' : ''}
                      </span>
                      <button
                        type="button"
                        className="board-clickup-filter-chip ci2-foot-toggle"
                        onClick={toggleAllGroups}
                        aria-label={
                          allCollapsed ? 'Expandir todos os grupos' : 'Recolher todos os grupos'
                        }
                      >
                        <span className="board-clickup-filter-chip-text">
                          <span className="board-clickup-filter-chip-label">
                            {allCollapsed ? 'Expandir grupos' : 'Recolher grupos'}
                          </span>
                        </span>
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="cp-cad-toolbar-bar">
            <div className="cp-cad-toolbar--pick">
              <button type="button" className="cp-cad-back-btn" onClick={backToPending}>
                ← Voltar
              </button>
              <div className="cp-cad-pick-banner">
                <CheckCircle2 size={14} strokeWidth={2} aria-hidden />
                <span>
                  Concluir cadastro de <strong>{selectedPending?.fullName || 'usuário'}</strong>
                  {selectedPending?.documentDigits
                    ? ` · ${formatDocByKind(selectedPending.documentDigits, selectedPending.documentKind)}`
                    : ' · sem documento (todos os clientes)'}
                </span>
              </div>
              <p className="cp-cad-pick-title">
                Vincule o usuário a um cliente ou grupo cadastrado
              </p>
            </div>
          </div>

          <div className="users-customers-surface">
            <div className="cp-cad-pick-table-bar">
              <BoardToolbarExpandSearch
                forceExpanded
                value={clientSearchQuery}
                onChange={setClientSearchQuery}
                placeholder="Filtrar (código, CNPJ, razão social…)"
                ariaLabel="Filtrar clientes para vincular"
              />
            </div>
            <div className="users-customers-table-wrap" role="region" aria-label="Selecionar cliente">
              <table className="users-customers-table">
                <thead>
                  <tr>
                    <th className="uc-col-codigo">Código</th>
                    <th className="uc-col-cnpj">CNPJ</th>
                    <th className="uc-col-nome-portal">Nome portal</th>
                    <th className="uc-col-razao">Razão Social</th>
                    <th className="uc-col-contratos">Contratos</th>
                    <th className="uc-col-usuarios">Usuários</th>
                    <th className="uc-col-action" aria-label="Ações" />
                  </tr>
                </thead>
                <tbody>
                  {clientLoading ? (
                    <tr>
                      <td className="users-customers-empty" colSpan={7}>
                        Carregando…
                      </td>
                    </tr>
                  ) : clientError ? (
                    <tr>
                      <td className="users-customers-empty" colSpan={7}>
                        {clientError}
                      </td>
                    </tr>
                  ) : clientRows.length === 0 ? (
                    <tr>
                      <td className="users-customers-empty" colSpan={7}>
                        Nenhum cliente encontrado para este documento.
                      </td>
                    </tr>
                  ) : filteredClients.length === 0 ? (
                    <tr>
                      <td className="users-customers-empty" colSpan={7}>
                        Nenhum cliente encontrado no filtro.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {groupedClients.grupos.map((row) => {
                        const expanded = expandedKeys.has(row.rowKey);
                        const integrantes = [row.primeiro, ...row.outros].filter(Boolean);
                        return (
                          <React.Fragment key={row.rowKey}>
                            <tr
                              className={[
                                'uc-section-row',
                                'uc-section-row--grupo',
                                expanded ? '' : 'uc-section-row--collapsed',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={() =>
                                setExpandedKeys((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(row.rowKey)) next.delete(row.rowKey);
                                  else next.add(row.rowKey);
                                  return next;
                                })
                              }
                            >
                              <td colSpan={6}>
                                <div className="uc-section-label">
                                  <span className="uc-section-chev" aria-hidden>
                                    <ChevronDown size={13} strokeWidth={2.5} />
                                  </span>
                                  <span
                                    className="uc-section-dot"
                                    style={{ background: UC_GRUPO_COLOR }}
                                    aria-hidden
                                  />
                                  <span className="uc-section-title">
                                    {row.grupoNome || 'Grupo'}
                                  </span>
                                  <span className="uc-section-count">· {integrantes.length}</span>
                                </div>
                              </td>
                              <td className="uc-cell-action uc-cell-sticky-action">
                                <button
                                  type="button"
                                  className="users-customers-add-btn uc-manage-users-add-btn cp-cad-pick-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openUserFormForRow(row);
                                  }}
                                >
                                  Selecionar
                                </button>
                              </td>
                            </tr>
                            {expanded
                              ? integrantes.map((sub, idx) =>
                                  sub ? (
                                    <tr
                                      key={`${row.rowKey}-sub-${sub.codigo}-${idx}`}
                                      className="users-customers-row users-customers-row--sub users-customers-row--cat-grupos"
                                    >
                                      {renderClientCells(row, { isSub: true, subCliente: sub })}
                                    </tr>
                                  ) : null
                                )
                              : null}
                          </React.Fragment>
                        );
                      })}
                      {groupedClients.semGrupos.length > 0 ? (
                        <React.Fragment key={UC_SEM_GRUPOS.key}>
                          <tr
                            className={[
                              'uc-section-row',
                              semGruposCollapsed ? 'uc-section-row--collapsed' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => setSemGruposCollapsed((v) => !v)}
                          >
                            <td colSpan={7}>
                              <div className="uc-section-label">
                                <span className="uc-section-chev" aria-hidden>
                                  <ChevronDown size={13} strokeWidth={2.5} />
                                </span>
                                <span
                                  className="uc-section-dot"
                                  style={{ background: UC_SEM_GRUPOS.color }}
                                  aria-hidden
                                />
                                <span className="uc-section-title">{UC_SEM_GRUPOS.label}</span>
                                <span className="uc-section-count">
                                  · {groupedClients.semGrupos.length}
                                </span>
                                <span className="uc-section-desc">{UC_SEM_GRUPOS.desc}</span>
                              </div>
                            </td>
                          </tr>
                          {!semGruposCollapsed
                            ? groupedClients.semGrupos.map((row) => (
                                <tr key={row.rowKey} className="users-customers-row">
                                  {renderClientCells(row)}
                                </tr>
                              ))
                            : null}
                        </React.Fragment>
                      ) : null}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {userForm &&
        createPortal(
          <div
            className={[
              'uc-user-side-overlay',
              'uc-user-side-overlay--portal',
              'uc-user-side-overlay--cadastro',
              userFormEntered ? 'uc-user-side-overlay--visible' : '',
            ].join(' ')}
            role="presentation"
          >
            <button
              type="button"
              className="uc-user-side-dim"
              onClick={closeUserForm}
              tabIndex={-1}
              aria-label="Fechar painel lateral"
              disabled={finishing}
            />
            <aside
              className={['uc-user-side-panel', userFormEntered ? 'uc-user-side-panel--open' : ''].join(
                ' '
              )}
              aria-label="Cadastro de usuário"
            >
              <div className="uc-user-side-head">
                <div className="uc-user-side-head-title">Novo usuário</div>
                <button
                  type="button"
                  className="uc-user-side-close"
                  onClick={closeUserForm}
                  aria-label="Fechar"
                  disabled={finishing}
                >
                  <X size={16} strokeWidth={2} aria-hidden />
                </button>
              </div>
              <div className="uc-user-side-body">
                <UserPortalFormPanel
                  key={`pending-${userForm.pending.id}-${userForm.scope.idGrupoRef}`}
                  mode="new"
                  user={null}
                  draft={userForm.draft}
                  idGrupoRef={userForm.scope.idGrupoRef}
                  codigosCliente={userForm.scope.codigosCliente || []}
                  onClose={closeUserForm}
                  onSave={handleSaveUser}
                  blockSaveTooltip={mailBlockTooltip}
                />
              </div>
            </aside>
          </div>,
          document.body
        )}
    </div>
  );
}
